import React, { useState, useMemo, useCallback } from 'react';
import { getMessage } from '@tcc/i18n';
import { Icon, Link } from '@tcc/crate';
import cx from 'classnames';
import { ApplicationsData, ApplicationProduct } from '../../../types/Application';
import { onClickApplicationReport } from '../../../utils/fieldFeatures';
import { sortApplicationProducts } from '../../../utils/applicationSorting';
import { SortField, SortDirection } from '../../../types/Sort';
import ApplicationHeader from './ApplicationHeader';
import ApplicationMetrics from './ApplicationMetrics';
import ProductTable from './ProductTable';
import styles from './ApplicationsPanel.css';

interface ApplicationsDetailsProps {
  applicationsData: ApplicationsData;
  testId?: string;
  expandedApplications?: Set<string>;
  onToggleApplicationExpand?: (applicationKey: string) => void;
  onApplicationSelect?: (applicationName: string, endDate: string) => void;
  season?: string;
  locale: string;
}


const ApplicationsDetails = ({
  applicationsData,
  testId = 'application-details',
  expandedApplications = new Set(),
  onToggleApplicationExpand,
  onApplicationSelect,
  season,
  locale
}: ApplicationsDetailsProps) => {
  const { applications } = applicationsData;
  const applicationCount = applications.length;
  
  const [sortField, setSortField] = useState<SortField>(SortField.NAME);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASC);

  const formatDate = useCallback((dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, { 
      weekday: 'short',
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }, [locale]);

  const sortProducts = useCallback((products: ApplicationProduct[]): ApplicationProduct[] => 
    sortApplicationProducts(products, sortField, sortDirection), [sortField, sortDirection]);

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC);
    } else {
      setSortField(field);
      setSortDirection(SortDirection.ASC);
    }
  }, [sortField, sortDirection]);

  const getSortIcon = useCallback((field: SortField) => {
    if (sortField !== field) return Icon.set.ARROW_UP;
    return sortDirection === SortDirection.ASC ? Icon.set.ARROW_UP : Icon.set.ARROW_DOWN;
  }, [sortField, sortDirection]);

  const sortedApplications = useMemo(() => 
    applications
      .slice()
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [applications]
  );

  const title = `${getMessage('application.card.title')} (${applicationCount})`;

  const handleReportClick = useCallback(() => {
    if (!season) {
      return;
    }
    const redirectToReport = onClickApplicationReport(season);
    redirectToReport();
  }, [season]);

  return (
    <div className={styles.applicationsPanelContainer} data-test-id={testId} data-testid={testId}>
      <div className={styles.applicationsPanelTitleRow}>
        <h2 className={styles.applicationsPanelTitle}>{title}</h2>
      </div>

      <div className={styles.applicationsPanelList}>
        {sortedApplications.map((application, index) => {
          const applicationKey = `${application.name}-${application.date}`;
          const isApplicationExpanded = expandedApplications.has(applicationKey);
          const testId = application.name === 'Headline' 
            ? `application-card-${application.name}`
            : `application-card-${index}`;
          
          return (
            <div 
              key={applicationKey}
              className={cx(
                styles.applicationsPanelItem,
                { [styles.applicationsPanelItemExpanded]: isApplicationExpanded }
              )}
              data-test-id={testId}
            >
              <ApplicationHeader
                name={application.name}
                date={application.date}
                thumbnailUri={application.thumbnailUri}
                isExpanded={isApplicationExpanded}
                onToggle={() => onToggleApplicationExpand?.(applicationKey)}
                onSelect={() => onApplicationSelect?.(application.name, String(new Date(application.date).getTime()))}
                testId={testId}
                formatDate={formatDate}
              />

              {isApplicationExpanded && application.details && (
                <div className={styles.applicationsPanelDetails} data-test-id={`${testId}-metrics`}>
                  <ApplicationMetrics
                    area={application.details.area}
                    applicationRate={application.details.applicationRate}
                    totalAmountApplied={application.details.totalAmountApplied}
                    machineSpeed={application.details.machineSpeed}
                  />
                  
                  <ProductTable
                    products={application.details.products}
                    sortField={sortField}
                    sortProducts={sortProducts}
                    handleSort={handleSort}
                    getSortIcon={getSortIcon}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <Link
        className={styles.applicationsPanelReportLink}
        onClick={handleReportClick}
        testId={`${testId}-report-link`}
      >
        {getMessage('application.report.button')}
      </Link>
    </div>
  );
};

export default ApplicationsDetails;
